import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Board } from '@/types';
import {
  getUserByUsername,
  getUserBoards,
  invalidateUsernameCache,
} from '@/services/user.service';
import { useAuth } from '@/contexts/AuthContext';
import {
  logPageView,
  logButtonClick,
  logError,
} from '@/services/analytics.service';
import { BoardList } from '@/components/organisms/BoardList';
import { CreateBoardDialog } from '@/components/organisms/CreateBoardDialog';
import { Loading } from '@/components/organisms/Loading';
import { FollowButton } from '@/components/molecules/FollowButton';
import { FollowersList } from '@/components/organisms/FollowersList';
import { FollowingList } from '@/components/organisms/FollowingList';
import { PageHeader } from '@/components/organisms/PageHeader';
import { Button } from '@/components/atoms/ui/button';
import { FiRefreshCw, FiPlus } from 'react-icons/fi';
import { config } from '@/lib/config';
import { getFollowCounts } from '@/services/follow.service';
import { UserAvatar } from '@/components/atoms/UserAvatar';

export const UserProfilePage = () => {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const {
    user: currentUser,
    firebaseUser: currentFirebaseUser,
    loading: authLoading,
    refreshUser,
  } = useAuth();

  const [profileUser, setProfileUser] = useState<User | null>(null);
  const [boards, setBoards] = useState<Board[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [createBoardDialogOpen, setCreateBoardDialogOpen] = useState(false);
  const [followersCount, setFollowersCount] = useState<number>(0);
  const [followingCount, setFollowingCount] = useState<number>(0);
  const [followersDialogOpen, setFollowersDialogOpen] = useState(false);
  const [followingDialogOpen, setFollowingDialogOpen] = useState(false);

  // Memoize isOwnProfile to prevent unnecessary recalculations
  const isOwnProfile = useMemo(
    () => currentUser?.username === username,
    [currentUser?.username, username]
  );

  // Get photo URL for avatar
  const photoURL =
    isOwnProfile && currentFirebaseUser?.photoURL
      ? currentFirebaseUser.photoURL
      : null;

  // Track current username and mounted state to prevent race conditions
  const currentUsernameRef = useRef<string | undefined>(username);
  const isMountedRef = useRef(true);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch follow counts
  const fetchFollowCounts = useCallback(async (userId: string) => {
    try {
      const counts = await getFollowCounts(userId);
      setFollowersCount(counts.followersCount);
      setFollowingCount(counts.followingCount);
    } catch (error) {
      console.error('Error fetching follow counts:', error);
    }
  }, []);

  // Refresh profile data
  const refreshProfile = useCallback(async () => {
    if (!username || authLoading) return;

    setRefreshing(true);
    logButtonClick('Refresh Profile', 'UserProfilePage');

    try {
      // Always invalidate memory cache first to force fresh fetch from Firestore
      invalidateUsernameCache(username);

      // If viewing own profile, refresh from AuthContext (which will fetch fresh data from Firestore)
      if (isOwnProfile && currentUser) {
        // Refresh user data from AuthContext (this will fetch fresh data from Firestore and update state)
        // Memory cache is already invalidated above, so getUser will fetch fresh data
        await refreshUser();
        // After refreshUser, AuthContext state will update and component will re-render
        // with the new currentUser. We'll use the updated currentUser from the next render.
        // For now, update with current data (will be updated on next render)
        setProfileUser(currentUser);
        const userBoards = getUserBoards(currentUser);
        setBoards(userBoards);
        await fetchFollowCounts(currentUser.uid);
      } else {
        // For other users' profiles, fetch fresh data (cache already invalidated)
        const user = await getUserByUsername(username);

        if (user) {
          setProfileUser(user);
          const userBoards = getUserBoards(user);

          // Filter out private boards if viewing someone else's profile
          const visibleBoards = userBoards.filter((board) => {
            if (board.isPublic) return true;
            if (currentUser && currentUser.uid === user.uid) return true;
            return false;
          });

          setBoards(visibleBoards);
          await fetchFollowCounts(user.uid);
        } else {
          setProfileUser(null);
        }
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to refresh profile';
      logError('Profile Refresh Error', errorMessage, 'UserProfilePage', {
        username,
      });
    } finally {
      // Keep button disabled and spinning for 2 seconds after refresh completes
      refreshTimeoutRef.current = setTimeout(() => {
        setRefreshing(false);
        refreshTimeoutRef.current = null;
      }, 2000);
    }
  }, [username, authLoading, isOwnProfile, currentUser, refreshUser, fetchFollowCounts]);

  // Update profile when currentUser changes after refresh (for own profile)
  useEffect(() => {
    if (isOwnProfile && currentUser && !refreshing) {
      setProfileUser(currentUser);
      const userBoards = getUserBoards(currentUser);
      setBoards(userBoards);
      fetchFollowCounts(currentUser.uid);
    }
  }, [isOwnProfile, currentUser, refreshing, fetchFollowCounts]);

  // Analytics: Only log when username changes
  useEffect(() => {
    if (username) {
      logPageView('User Profile Page', {
        username,
        is_own_profile: isOwnProfile,
      });
    }
  }, [username, isOwnProfile]);

  useEffect(() => {
    // Update ref when username changes
    currentUsernameRef.current = username;
    isMountedRef.current = true;
    setLoading(true);

    const fetchData = async () => {
      if (!username) {
        if (isMountedRef.current) {
          setLoading(false);
        }
        return;
      }

      // Wait for auth to finish loading before determining if it's own profile
      if (authLoading) {
        // Auth is still loading, wait for it to complete
        // The effect will re-run when authLoading becomes false
        return;
      }

      // If viewing own profile, use data from AuthContext (already has real-time listener)
      if (isOwnProfile && currentUser) {
        // Check if still mounted and username hasn't changed
        if (!isMountedRef.current || currentUsernameRef.current !== username) {
          return;
        }
        setProfileUser(currentUser);
        const userBoards = getUserBoards(currentUser);
        setBoards(userBoards); // Own boards are always visible
        setLoading(false);
        return;
      }

      // For other users' profiles, fetch once (no listener needed)
      // Only fetch if we're sure it's not our own profile (auth has loaded and isOwnProfile is false)
      try {
        const user = await getUserByUsername(username);

        // Check if still mounted and username hasn't changed (race condition prevention)
        if (!isMountedRef.current || currentUsernameRef.current !== username) {
          return;
        }

        if (user) {
          setProfileUser(user);
          // Get boards from denormalized user data (no additional query needed!)
          const userBoards = getUserBoards(user);

          // Filter out private boards if viewing someone else's profile
          const visibleBoards = userBoards.filter((board) => {
            if (board.isPublic) return true;
            if (currentUser && currentUser.uid === user.uid) return true;
            return false;
          });

          setBoards(visibleBoards);
          fetchFollowCounts(user.uid);
        } else {
          // User not found
          setProfileUser(null);
        }
      } catch (error) {
        // Only handle error if still mounted and username hasn't changed
        if (!isMountedRef.current || currentUsernameRef.current !== username) {
          return;
        }
        console.error('Error fetching user data:', error);
        const errorMessage =
          error instanceof Error ? error.message : 'Failed to fetch user data';
        logError('User Profile Fetch Error', errorMessage, 'UserProfilePage', {
          username,
        });
        setProfileUser(null);
      } finally {
        // Only update loading state if still mounted and username hasn't changed
        if (isMountedRef.current && currentUsernameRef.current === username) {
          setLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    username,
    authLoading,
    // Only depend on currentUser when viewing own profile to avoid unnecessary re-fetches
    // when viewing someone else's profile. isOwnProfile is derived from these, so we don't need it separately.
    // Note: currentUser and isOwnProfile are intentionally excluded to prevent unnecessary re-fetches
    // Complex expression is intentional - only depend on currentUser when viewing own profile
    // eslint-disable-next-line react-hooks/exhaustive-deps
    isOwnProfile ? currentUser : null,
  ]);

  // Cleanup refresh timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  if (loading) {
    return <Loading message="Loading profile..." />;
  }

  if (!profileUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2 text-foreground">User not found</h2>
          <p className="text-muted-foreground mb-4">
            The user "{username}" does not exist
          </p>
          <Button
            onClick={() => {
              logButtonClick('Back', 'UserProfilePage');
              navigate(-1);
            }}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PageHeader
        title={isOwnProfile ? 'My Profile' : `${username}'s Profile`}
        showBackButton
      />

      {/* Profile Info */}
      <section className="border-b border-border bg-muted/50">
        <div className="max-w-[1200px] mx-auto px-4 py-8">
          <div className="flex items-start gap-6">
            <UserAvatar
              photoURL={photoURL}
              username={profileUser.username}
              size="xl"
            />
            <div className="flex-1">
              <div className="flex items-center gap-4 mb-2">
                <h2 className="text-3xl font-bold text-foreground">
                  {profileUser.username}
                </h2>
                {!isOwnProfile && (
                  <FollowButton
                    userId={profileUser.uid}
                    username={profileUser.username}
                    onFollowChange={() => {
                      fetchFollowCounts(profileUser.uid);
                    }}
                  />
                )}
              </div>
              <p className="text-muted-foreground mb-4">{profileUser.email}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-card rounded-lg p-4 border border-border">
                  <p className="text-sm text-muted-foreground">Boards</p>
                  <p className="text-2xl font-bold text-foreground">
                    {boards.length} / {config.maxBoardsPerUser}
                  </p>
                </div>
                <button
                  onClick={() => {
                    logButtonClick('View Followers', 'UserProfilePage');
                    setFollowersDialogOpen(true);
                  }}
                  className="bg-card rounded-lg p-4 border border-border hover:bg-accent transition-colors text-left"
                >
                  <p className="text-sm text-muted-foreground">Followers</p>
                  <p className="text-2xl font-bold text-foreground">
                    {followersCount}
                  </p>
                </button>
                <button
                  onClick={() => {
                    logButtonClick('View Following', 'UserProfilePage');
                    setFollowingDialogOpen(true);
                  }}
                  className="bg-card rounded-lg p-4 border border-border hover:bg-accent transition-colors text-left"
                >
                  <p className="text-sm text-muted-foreground">Following</p>
                  <p className="text-2xl font-bold text-foreground">
                    {followingCount}
                  </p>
                </button>
                {isOwnProfile && (
                  <div className="bg-card rounded-lg p-4 border border-border">
                    <p className="text-sm text-muted-foreground">Pixel Quota</p>
                    <p className="text-2xl font-bold text-foreground">
                      {profileUser.pixelQuota}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Boards Section */}
      <section className="max-w-[1200px] mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-bold text-foreground">
                {isOwnProfile ? 'My Boards' : `${username}'s Boards`}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={refreshProfile}
                disabled={refreshing || loading}
                className="h-8 w-8 hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                title="Refresh profile"
              >
                <FiRefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {isOwnProfile && (
              <>
                <Button
                  onClick={() => {
                    logButtonClick('New Board', 'UserProfilePage');
                    setCreateBoardDialogOpen(true);
                  }}
                  className="flex items-center gap-2"
                >
                  <FiPlus className="h-4 w-4" />
                  New Board
                </Button>
                <CreateBoardDialog
                  open={createBoardDialogOpen}
                  onOpenChange={setCreateBoardDialogOpen}
                />
              </>
            )}
          </div>
          <p className="text-muted-foreground">
            {boards.length} board{boards.length !== 1 ? 's' : ''}
          </p>
        </div>

        <BoardList boards={boards} loading={false} />
      </section>

      {/* Follow Dialogs */}
      {profileUser && (
        <>
          <FollowersList
            userId={profileUser.uid}
            username={profileUser.username}
            open={followersDialogOpen}
            onOpenChange={setFollowersDialogOpen}
          />
          <FollowingList
            userId={profileUser.uid}
            username={profileUser.username}
            open={followingDialogOpen}
            onOpenChange={setFollowingDialogOpen}
          />
        </>
      )}
    </div>
  );
};
